import { trialIdentificationService } from '@/services/trial-identification-service'
import { trialIdentificationStatusService } from '@/services/trial-identification-status-service'
import { patientEligibilityVerificationService } from '@/services/patient-eligibility-verification-service'
import { patientEligibilityNoteService } from '@/services/patient-eligibility-note-service'
import { normalize, denormalize } from 'normalizr'
import { schemas } from '@/store/schemas'
import _ from 'lodash'
import uuid from 'uuid'
import Vue from 'vue'

export const mutations = {
  ['SET_TRIAL_IDENTIFICATIONS'](state, trials) {
    const result = normalize(trials, schemas.trialIdentificationList)
    state.trialIdentifications = {
      ...state.trialIdentifications,
      ...result.entities.trialIdentifications
    }
    state.organizationTrials = {
      ...state.organizationTrials,
      ...result.entities.organizationTrials
    }
    state.organizations = { ...state.organizations, ...result.entities.organizations }
    state.patients = { ...state.patients, ...result.entities.patients }
  },
  ['SET_IDENTIFIED_TRIAL_ELIGIBILITY_DETAILS'](state, eligibilityCriteria) {
    const inclusionResult = normalize(eligibilityCriteria.inclusion, schemas.trialCriteriaDTOList)
    state.inclusions = inclusionResult.entities.trialCriteriaDTOs

    const exclusionResult = normalize(eligibilityCriteria.exclusion, schemas.trialCriteriaDTOList)
    state.exclusions = exclusionResult.entities.trialCriteriaDTOs
    state.trialEligibilities = {
      ...inclusionResult.entities.trialEligibilities,
      ...exclusionResult.entities.trialEligibilities
    }
    state.patientEligibilityNotes = {
      ...inclusionResult.entities.patientEligibilityNotes,
      ...exclusionResult.entities.patientEligibilityNotes
    }
    state.patientEligibilityVerifications = {
      ...inclusionResult.entities.patientEligibilityVerifications,
      ...exclusionResult.entities.patientEligibilityVerifications
    }
    state.patientEligibilityEvaluationResults = {
      ...inclusionResult.entities.patientEligibilityEvaluationResults,
      ...exclusionResult.entities.patientEligibilityEvaluationResults
    }
  },
  ['REFRESH_TRIAL_IDENTIFICATIONS'](state) {
    state.refreshTrialIdentifications = uuid()
  },
  ['REMOVE_PATIENT_ELIGIBILITY_NOTE'](state, noteId) {
    Vue.delete(state.patientEligibilityNotes, noteId)
  }
}

export const actions = {
  async ['GET_TRIAL_IDENTIFICATIONS']({ commit }, { orgId, trialId, params }) {
    const response = await trialIdentificationService.getTrialIdentifications(
      orgId,
      trialId,
      params
    )
    commit('SET_TRIAL_IDENTIFICATIONS', response.data)
    return response
  },
  async ['GET_TRIAL_IDENTIFICATIONS_PAGED'](
    { commit },
    { orgId, trialId, params, cancelToken = null }
  ) {
    params = { ...params, ...{ paged: true } }
    try {
      const response = await trialIdentificationService.getTrialIdentifications(
        orgId,
        trialId,
        params,
        cancelToken
      )
      commit('SET_TRIAL_IDENTIFICATIONS', response.data.content)
      return response.data
    } catch (e) {
      commit('SET_TRIAL_IDENTIFICATIONS', [])
    }
  },
  async ['GET_IDENTIFIED_TRIAL_ELIGIBILITY_DETAILS']({ commit }, { orgId, trialId, params }) {
    const response = await trialIdentificationService.getIdentifiedTrialEligibilityDetails(
      orgId,
      trialId,
      params
    )
    commit('SET_IDENTIFIED_TRIAL_ELIGIBILITY_DETAILS', response.data)
  },
  async ['ADD_TRIAL_IDENTIFICATION_STATUS']({ dispatch }, { orgId, status }) {
    const response = await trialIdentificationStatusService.createTrialIdentificiationStatus(
      orgId,
      status
    )
    return response.data
  },
  async ['SAVE_PATIENT_ELIGIBILITY_VERIFICATION'](
    { getters, state },
    { orgId, verification, trial }
  ) {
    const trialEligibility = denormalize(
      verification.trialEligibility,
      schemas.trialEligibility,
      state
    )
    trialEligibility.trial = trial
    const body = {
      patient: verification.patient,
      trialEligibility: trialEligibility,
      status: verification.status,
      organizationTrial: verification.organizationTrial
    }

    await patientEligibilityVerificationService.save(orgId, body)
  },
  async ['ADD_PATIENT_ELIGIBILITY_NOTE']({}, { orgId, note }) {
    return await patientEligibilityNoteService.createNote(orgId, note)
  },
  async ['DELETE_PATIENT_ELIGIBILITY_NOTE']({ commit }, { orgId, noteId }) {
    await patientEligibilityNoteService.deleteNote(orgId, noteId)
    commit('REMOVE_PATIENT_ELIGIBILITY_NOTE', noteId)
  },
  ['REFRESH_TRIAL_IDENTIFICATIONS']({ commit }) {
    commit('REFRESH_TRIAL_IDENTIFICATIONS')
  }
}

export const getters = {
  getStatusByTrialIdentificationId: state => id => {
    return state.trialIdentifications[id].latestStatus
  },
  getCriteriaByStatus: state => status => {
    if (_.toLower(status) === 'inclusion') {
      return state.inclusions
    }
    if (_.toLower(status) === 'exclusion') {
      return state.exclusions
    }
    return {}
  },
  getTrialEligibilityViewModel: state => id => {
    if (id) {
      return createTrialEligibilityViewModel(state, id)
    }
    return _.map(_.keys(state.trialEligibilities), id => createTrialEligibilityViewModel(state, id))
  },
  getTrialIdentificationsByTrialId: (state, getters) => trialId => {
    let trialIdentificationsForTrialId = []
    _.forEach(state.trialIdentifications, ti => {
      const denormalizedTrialId = denormalize(
        ti.id,
        schemas.trialIdentification,
        getters.getNormalizedState()
      )
      if (
        !_.isNil(denormalizedTrialId.organizationTrial) &&
        denormalizedTrialId.organizationTrial.trial.id === trialId
      ) {
        trialIdentificationsForTrialId.push(ti)
      }
    })
    return trialIdentificationsForTrialId
  },
  getTrialIdentificationByTrialAndPatientId: (state, getters) => (trialId, patientId) => {
    let trialIdForTrialIdAndPatient = {}
    _.forEach(state.trialIdentifications, ti => {
      const denormalizedTrialId = denormalize(
        ti.id,
        schemas.trialIdentification,
        getters.getNormalizedState()
      )
      if (
        denormalizedTrialId.patient.id === patientId &&
        denormalizedTrialId.organizationTrial.trial.id === trialId
      ) {
        trialIdForTrialIdAndPatient = ti
      }
    })
    return trialIdForTrialIdAndPatient
  }
}

function createTrialEligibilityViewModel(state, id) {
  const trialEligibility = _.clone(state.trialEligibilities[id])
  const verification = _.clone(
    _.find(_.values(state.patientEligibilityVerifications), it => it.trialEligibility === id)
  )
  const notes = _.map(
    _.filter(_.values(state.patientEligibilityNotes), it => it.trialEligibility === id)
  )
  const patientEligibilityEvaluationResults = state.patientEligibilityEvaluationResults[id]
  return {
    id: trialEligibility.id,
    trialEligibility: trialEligibility,
    patientEligibilityVerification: verification,
    patientEligibilityNotes: notes,
    patientEvaluationResults: patientEligibilityEvaluationResults
  }
}
